import { useEffect, useRef, useState } from 'react';
import useConfigurationBatchSettings from '../Hooks/useConfigurationBatchSettings.js';
import NewBatchFileValidationWorker from '../Workers/NewBatchFileValidationWorker.js';
import BatchFileValidationWorker from '../Workers/BatchFileValidationWorker.js';
import { enableNewBatchSubmitValidation } from '../Utils/features.js';

const workerRef = enableNewBatchSubmitValidation 
    ? new Worker(NewBatchFileValidationWorker)
    : new Worker(BatchFileValidationWorker);

const useBatchValidation = (enable837 = false) => {
    const [status, setStatus] = useState('');
    const [messages, setMessages] = useState([]);
    const [progress, setProgress] = useState(0);
    const { batchSettings, loading, error } = useConfigurationBatchSettings();
    const fileReaderRef = useRef();

    useEffect(() => {
        return () => {
            workerRef.postMessage({
                type: 'CANCELLATION'
            });
            workerRef.terminate();
        };
    }, []);

    useEffect(() => {
        if (!loading && batchSettings.length > 0) {
            initializeWorker(batchSettings);
        }
    }, [loading, batchSettings]);

    const initializeWorker = (config) => {
        workerRef.postMessage({
            type: 'INIT_CONFIG',
            config
        });
    };

    const validate = (file) => {
        if (status === 'InProgress') {
            reset();
        }

        setStatus('Loading');

        fileReaderRef.current = new FileReader();
        fileReaderRef.current.onprogress = (event) => {
            setProgress((event.loaded / event.total) * 100);
        };

        fileReaderRef.current.onload = (event) => {
            const fileContents = event.target.result;

            // If enable837 is true then we skip zip file validation and validation on a non tab delimeted file
            if (enable837) {
                if (fileContents instanceof ArrayBuffer) {
                    // Check if file is a zip archive by examining the magic number
                    const isZip = result.byteLength >= 4 && 
                                  new DataView(result).getUint32(0) === 0x504b0304;
    
                    if (isZip) {
                        setStatus('Skipped');
                        setProgress(100);
                        setMessages(['Validation skipped: file is a zip archive']);
                        return;
                    }
    
                    // Handle other binary files as needed
                    setStatus('Error');
                    setMessages(['Error: Unexpected binary file format']);
                    setProgress(100);
                    return;
                }
    
                if (!fileContents.includes('\t')) {
                    setStatus('Skipped');
                    setProgress(100);
                    setMessages(['Validation skipped: file is not tab delimited']);
                    return;
                }
            }

            setStatus('InProgress');
            setProgress(0);

            workerRef.onmessage = ({ data }) => {
                const validationResults = data.data;
                setMessages(validationResults.messages);
                setProgress(validationResults.progress);
                setStatus(validationResults.status);
            };

            workerRef.postMessage({
                type: 'VALIDATION',
                request: {
                    fileContents
                }
            });
        };

        fileReaderRef.current.readAsText(file);
    };

    const reset = () => {
        setStatus('');
        setMessages([]);
        setProgress(0);
        fileReaderRef.current?.abort();
        fileReaderRef.current = null;

        workerRef.postMessage({
            type: 'CANCELLATION'
        });
    };

    return {
        status,
        validate,
        messages,
        progress,
        reset
    };
};

export default useBatchValidation;
